import { Button } from '@components/ui/Button'
import styles from './styles.module.scss'
import classNames from 'classnames'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'
import { useEffect, useRef, useState } from 'react'

interface CollectionProductFamilyFilterProps {
  filters: string[]
  selectedFilter?: string
  selectProductFamilyFilter: (filter: string) => void
}

export const CollectionProductFamilyFilter = ({
  filters,
  selectedFilter,
  selectProductFamilyFilter
}: CollectionProductFamilyFilterProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [scrollMask, setScrollMask] = useState('right')
  const isTabletOrUp = useMediaQuery({
    minWidth: BREAKPOINT_SIZES.TABLET,
    initialState: true
  })
  const scrollBreakpoint = isTabletOrUp ? 6 : 2

  const filterClasses = classNames(styles.filterContainer, {
    [styles.maskBoth]: scrollBreakpoint && scrollMask === 'both',
    [styles.maskLeft]: scrollBreakpoint && scrollMask === 'left',
    [styles.maskRight]: scrollBreakpoint && scrollMask === 'right'
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    function updateScrollMask() {
      if (!el) return
      const { scrollLeft, scrollWidth, clientWidth } = el
      const canScroll = scrollWidth > clientWidth

      if (!canScroll) {
        setScrollMask('none')
        return
      }

      const atStart = scrollLeft <= 0
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

      if (atStart && !atEnd) setScrollMask('right')
      else if (!atStart && atEnd) setScrollMask('left')
      else if (!atStart && !atEnd) setScrollMask('both')
      else setScrollMask('none')
    }

    updateScrollMask()

    el.addEventListener('scroll', updateScrollMask)
    window.addEventListener('resize', updateScrollMask)

    const observer = new MutationObserver(updateScrollMask)
    observer.observe(el, { childList: true, subtree: true })

    return () => {
      el.removeEventListener('scroll', updateScrollMask)
      window.removeEventListener('resize', updateScrollMask)
      observer.disconnect()
    }
  }, [])

  return (
    <div className={styles.filterWrapper}>
      <div className={filterClasses} ref={containerRef}>
        {filters.map((filter, index) => (
          <Button
            key={index}
            classes={styles.filterButton}
            variant="secondary"
            fullWidth="never"
            size="small"
            textSize="large"
            selected={filter === selectedFilter}
            onClick={() => selectProductFamilyFilter(filter)}
          >
            {filter}
          </Button>
        ))}
      </div>
    </div>
  )
}
